import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { PROJECTS, OTHER_PROJECTS } from "@/data/content";
import { useSeo } from "@/lib/seo";

const ProjectCard = ({ p, i }) => (
  <Reveal delay={(i % 4) * 0.07} className="h-full">
    <Link
      to={`/progetti/${p.slug}`}
      data-testid={`project-card-${p.slug}`}
      className="group relative block aspect-[9/16] w-full overflow-hidden rounded-xl bg-navy text-left shadow-[0_20px_50px_-30px_rgba(10,18,41,0.5)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_32px_70px_-28px_rgba(37,99,235,0.45)]"
      aria-label={`Apri il progetto ${p.client}`}
    >
      <video
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        src={p.video}
        poster={p.poster}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        aria-label={`Video del progetto ${p.client}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/10 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
      <span className="absolute left-3 top-3 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[9px] font-semibold tracking-[0.2em] text-white backdrop-blur-md">
        {p.stat}
      </span>
      <div className="absolute inset-x-0 bottom-0 p-5">
        <p className="font-mono text-[9px] tracking-[0.25em] text-brand-cyan">{p.sector.toUpperCase()}</p>
        <h3 className="mt-1.5 font-display text-lg font-black leading-tight tracking-tight text-white sm:text-xl">{p.client}</h3>
        <p className="font-display text-[10px] font-bold tracking-[0.3em] text-white/50">{p.location}</p>
        <span className="mt-3 inline-flex translate-y-2 items-center gap-2 rounded-full bg-white/10 px-4 py-2 font-display text-[11px] font-bold tracking-[0.15em] text-white opacity-0 backdrop-blur-md transition-all duration-400 group-hover:translate-y-0 group-hover:opacity-100">
          SCOPRI IL PROGETTO <ArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  </Reveal>
);

export const ProjectsPage = () => {
  const [expanded, setExpanded] = useState(false);
  useSeo({
    title: "Progetti | Starting Point Agency",
    description: "Case study reali: Mister Kebab, Drago Ramen, VB Beauty Lab Academy, Panificio Cirolli. Punto di partenza, strategia, attività realizzate e risultati verificabili.",
  });

  return (
    <div data-testid="projects-page" className="bg-white">
      <section className="bg-white bg-grid-light pb-20 pt-32 lg:pt-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Kicker>Progetti</Kicker>
            <h1 className="mt-5 max-w-3xl font-display text-5xl font-black leading-[0.95] tracking-tight text-navy sm:text-7xl">
              I NOSTRI <span className="text-brand-gradient">PROGETTI.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              Non una galleria di loghi. Ogni progetto racconta il punto di partenza del cliente, la strategia adottata, le attività realizzate e i risultati documentabili.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROJECTS.map((p, i) => (
              <ProjectCard key={p.slug} p={p} i={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-brand-light py-20 lg:py-28" data-testid="other-projects">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-3xl font-display text-3xl font-black leading-[1.02] tracking-tight text-navy sm:text-5xl">
              E QUESTI SONO SOLO <span className="text-brand-gradient">ALCUNI DEI PROGETTI.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
              L'esperienza dell'agenzia comprende anche altri progetti di digital marketing e comunicazione online e offline: video, grafica, stampa, branding e materiali pubblicitari.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <button
              type="button"
              data-testid="other-projects-toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-8 inline-flex items-center gap-3 rounded-full border border-navy/20 px-6 py-3 font-display text-xs font-bold tracking-[0.18em] text-navy transition-colors hover:border-brand-blue hover:text-brand-blue"
            >
              {expanded ? "NASCONDI GLI ALTRI PROGETTI" : "MOSTRA GLI ALTRI PROGETTI"}
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </Reveal>

          {expanded && (
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {OTHER_PROJECTS.map((p) => (
                <a
                  key={p.slug}
                  href={p.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`other-project-${p.slug}`}
                  className="group flex gap-5 border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-30px_rgba(37,99,235,0.4)]"
                >
                  <div className="relative w-24 shrink-0 overflow-hidden rounded-lg bg-navy">
                    <div className="aspect-[9/16] w-full">
                      <video className="h-full w-full object-cover" src={p.video} poster={p.poster} muted loop playsInline autoPlay preload="metadata" aria-label={`Video del progetto ${p.client}`} />
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-brand-blue">{p.location}</p>
                    <h3 className="mt-1 font-display text-base font-black tracking-tight text-navy group-hover:text-brand-blue">{p.client}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">{p.text}</p>
                  </div>
                </a>
              ))}
              <Link
                to="/"
                data-testid="other-project-future"
                className="group flex min-h-[160px] flex-col items-start justify-center gap-3 border border-dashed border-navy/25 bg-transparent p-6 transition-colors hover:border-brand-blue"
              >
                <span className="font-display text-base font-black tracking-tight text-navy group-hover:text-brand-blue">
                  IL PROSSIMO PROGETTO POTREBBE ESSERE IL TUO.
                </span>
                <span className="inline-flex items-center gap-2 font-display text-xs font-bold tracking-[0.18em] text-navy/50 group-hover:text-brand-blue">
                  PARLIAMONE <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
